// D-D.6 — Matter phases card (firm-side billing phases).
//
// Phases are the FIRM's internal billing structure ("MSJ Prep", "Trial",
// "Appeal") with a budget each, so partners can see burn phase-by-phase.
// Deliberately distinct from MatterStageCard, which is the CLIENT-facing
// progress timeline — a client sees "Discovery", not "we budgeted 40 hours
// for depositions".  Both live on the Attorney tab; only stages reach the portal.
//
// The logged_hours / logged_amount columns come from the server's aggregation
// over time_entries.matter_phase_id, counting status IN ('posted','billed')
// only — unposted work is not yet firm revenue, so it does not count toward burn.

import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import api from '../services/api'
import { useAsk } from '../context/PromptProvider'
import { useToast } from '../context/ToastContext'
import { parseDateOnly } from '../utils/format'

const STATUSES = [
  { value: 'planned', label: 'Planned', chip: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In progress', chip: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', chip: 'bg-emerald-100 text-emerald-700' },
  { value: 'billed', label: 'Billed', chip: 'bg-purple-100 text-purple-700' },
]
const chipFor = v => STATUSES.find(s => s.value === v)?.chip || 'bg-gray-100 text-gray-700'
const labelFor = v => STATUSES.find(s => s.value === v)?.label || v

const money = n => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
// Date-only columns must never go through new Date() — that parses as UTC
// midnight and renders as the previous day west of Greenwich.
const shortDate = d => (d ? format(parseDateOnly(d), 'MMM d, yyyy') : null)

export default function MatterPhasesCard ({ matterId, canEdit }) {
  const [phases, setPhases] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ phase_name: '', budget: '', description: '' })
  const toast = useToast()
  const ask = useAsk()

  async function load () {
    setLoading(true)
    try {
      const r = await api.get(`/api/matter-phases/matter/${matterId}`)
      setPhases(r.data || [])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load phases')
    } finally {
      setLoading(false)
    }
  }

  // load is recreated each render; matterId is the only real trigger.
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => { load() }, [matterId])

  async function addPhase () {
    if (!form.phase_name.trim()) { toast.error('Phase name is required'); return }
    if (form.budget !== '' && (isNaN(parseFloat(form.budget)) || parseFloat(form.budget) < 0)) {
      toast.error('Budget must be a non-negative number'); return
    }
    setSaving(true)
    try {
      await api.post('/api/matter-phases', {
        matter_id: matterId,
        phase_name: form.phase_name.trim(),
        budget: form.budget === '' ? null : parseFloat(form.budget),
        description: form.description.trim() || null,
        sort_order: phases.length,
      })
      toast.success('Phase added')
      setForm({ phase_name: '', budget: '', description: '' })
      setAdding(false)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add phase')
    } finally {
      setSaving(false)
    }
  }

  async function changeStatus (phase, status) {
    if (status === phase.status) return
    try {
      await api.put(`/api/matter-phases/${phase.id}`, { status })
      toast.success(`"${phase.phase_name}" → ${labelFor(status)}`)
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update phase')
    }
  }

  async function removePhase (phase) {
    const logged = Number(phase.logged_hours) || 0
    const ok = await ask({
      title: 'Remove this phase?',
      message: logged > 0
        ? `"${phase.phase_name}" has ${logged.toFixed(1)} hrs logged against it. Those time entries are KEPT — they just stop being grouped under this phase.`
        : `This removes "${phase.phase_name}" from this matter.`,
      confirmLabel: 'Remove',
      // type:'confirm' + danger are the real prop names. PromptProvider defaults
      // type to 'text', so omitting it renders a TEXT BOX and resolves to a
      // string instead of a boolean -- the documented dropped-prop trap.
      type: 'confirm',
      danger: true,
    })
    if (!ok) return
    try {
      await api.delete(`/api/matter-phases/${phase.id}`)
      toast.success('Phase removed')
      await load()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove phase')
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-4">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">🧩 Billing Phases</h3>
          <p className="text-xs text-gray-500 mt-1">
            Internal billing structure for this matter. Tag time entries to a phase to track burn
            against each budget. Not visible to the client — that's the Matter Stage timeline.
          </p>
        </div>
        {canEdit && !adding && (
          <button onClick={() => setAdding(true)}
            className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            + Add phase
          </button>
        )}
      </div>

      {adding && (
        <div className="px-6 py-4 border-b border-gray-100 bg-primary-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label htmlFor="phase-name" className="block text-xs font-medium text-gray-700 mb-1">Phase name</label>
              <input id="phase-name" type="text" maxLength={120} value={form.phase_name}
                onChange={e => setForm({ ...form, phase_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="MSJ Prep" autoFocus />
            </div>
            <div>
              <label htmlFor="phase-budget" className="block text-xs font-medium text-gray-700 mb-1">Budget ($, optional)</label>
              <input id="phase-budget" type="number" step="0.01" min="0" value={form.budget}
                onChange={e => setForm({ ...form, budget: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="5000.00" />
            </div>
            <div>
              <label htmlFor="phase-desc" className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
              <input id="phase-desc" type="text" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Draft + file motion" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={addPhase} disabled={saving}
              className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save phase'}
            </button>
            <button onClick={() => { setAdding(false); setForm({ phase_name: '', budget: '', description: '' }) }}
              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      <div className="px-6 py-4">
        {loading ? (
          <p className="text-sm text-gray-500">Loading phases…</p>
        ) : phases.length === 0 ? (
          <p className="text-sm text-gray-500">
            No phases yet.{canEdit ? ' Add one to start tracking burn by phase.' : ''}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="pb-2 pr-3">Phase</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3 text-right">Budget</th>
                  <th className="pb-2 pr-3 text-right">Logged</th>
                  <th className="pb-2 pr-3">Burn</th>
                  {canEdit && <th className="pb-2" />}
                </tr>
              </thead>
              <tbody>
                {phases.map(p => {
                  const budget = p.budget == null ? null : Number(p.budget)
                  const spent = Number(p.logged_amount) || 0
                  const pct = budget && budget > 0 ? Math.round((spent / budget) * 100) : null
                  const bar = pct == null ? 'bg-gray-300' : pct > 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'
                  return (
                    <tr key={p.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800">{p.phase_name}</div>
                        {p.description && <div className="text-xs text-gray-500">{p.description}</div>}
                        {(p.started_at || p.completed_at) && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {p.started_at && `Started ${shortDate(p.started_at)}`}
                            {p.started_at && p.completed_at && ' · '}
                            {p.completed_at && `Completed ${shortDate(p.completed_at)}`}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {canEdit && p.status !== 'billed' ? (
                          <select value={p.status} onChange={e => changeStatus(p, e.target.value)}
                            aria-label={`Status for ${p.phase_name}`}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-xs">
                            {STATUSES.filter(s => s.value !== 'billed').map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${chipFor(p.status)}`}>{labelFor(p.status)}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-700">
                        {budget == null ? <span className="text-gray-400">—</span> : money(budget)}
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-700">
                        <div>{(Number(p.logged_hours) || 0).toFixed(1)} hrs</div>
                        <div className="text-xs text-gray-500">{money(spent)}</div>
                      </td>
                      <td className="py-2 pr-3 w-32">
                        {pct == null ? (
                          <span className="text-xs text-gray-400">no budget set</span>
                        ) : (
                          <>
                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full ${bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <div className={`text-xs mt-0.5 ${pct > 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                              {pct}%{pct > 100 ? ' over' : ''}
                            </div>
                          </>
                        )}
                      </td>
                      {canEdit && (
                        <td className="py-2 text-right">
                          <button onClick={() => removePhase(p)}
                            className="text-xs text-red-600 hover:text-red-800">Remove</button>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
